import { useEffect, useState, useCallback } from 'react';
import { Users, UserPlus, X, ChevronDown, ChevronRight, Check, Star, Trash2, Pencil } from 'lucide-react';
import Layout from '../components/Layout.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Lead { userId: string; email: string; }
interface Department {
  id: string;
  name: string;
  member_count: number;
  leads: Lead[];
  leadUserIds: string[];
}

interface DeptMember {
  userId: string;
  email: string;
  role: string;
  isLead: boolean;
  cards: Array<{ id: string; slug: string }>;
}

interface OrgUser {
  id: string;
  email: string;
  role: string;
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputCls = 'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';
const btnPrimary = 'inline-flex items-center gap-2 rounded-full bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity';
const btnGhost = 'inline-flex items-center gap-2 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink-muted hover:bg-raised hover:text-ink transition-all';
const btnDanger = 'inline-flex items-center gap-2 rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-3 py-1.5 text-xs text-danger-700 dark:text-danger-300 hover:bg-danger-500/20 hover:border-danger-500/50 transition-all';

function RoleBadge({ role }: { role: string }) {
  const { t } = useAppTranslation();
  const map: Record<string, string> = {
    owner: 'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300',
    admin: 'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300',
    department_lead: 'bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-300',
    member: 'bg-raised text-ink-muted',
    superadmin: 'bg-danger-100 dark:bg-danger-500/20 text-danger-700 dark:text-danger-300',
  };
  const labels: Record<string, string> = {
    owner: t('common.roles.owner'),
    admin: t('common.roles.admin'),
    department_lead: t('departments.leadBadge'),
    member: t('common.roles.member'),
    superadmin: 'Superadmin',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${map[role] ?? map['member']}`}>
      {labels[role] ?? role}
    </span>
  );
}

// ─── DeptDetail — expanded view ───────────────────────────────────────────────

function DeptDetail({
  dept,
  orgUsers,
  canEdit,
  onRefresh,
}: {
  dept: Department;
  orgUsers: OrgUser[];
  canEdit: boolean;
  onRefresh: () => void;
}) {
  const { t } = useAppTranslation();
  const [members, setMembers] = useState<DeptMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`/api/admin/departments/${dept.id}/members`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { members: DeptMember[] };
        setMembers(data.members ?? []);
      }
    } finally {
      setLoadingMembers(false);
    }
  }, [dept.id]);

  useEffect(() => { void loadMembers(); }, [loadMembers]);

  const memberIds = new Set(members.map((m) => m.userId));
  const availableUsers = orgUsers.filter(
    (u) => !memberIds.has(u.id) && search && u.email.toLowerCase().includes(search.toLowerCase())
  );

  async function addMember(userId: string) {
    setSaving(userId);
    try {
      await fetch(`/api/admin/departments/${dept.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userIds: [userId] }),
      });
      setSearch('');
      setShowAddUser(false);
      await loadMembers();
      onRefresh();
    } finally {
      setSaving(null);
    }
  }

  async function removeMember(userId: string) {
    if (!confirm(t('departments.confirmRemoveMember'))) return;
    setSaving(userId);
    try {
      await fetch(`/api/admin/departments/${dept.id}/members/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      await loadMembers();
      onRefresh();
    } finally {
      setSaving(null);
    }
  }

  async function toggleLead(userId: string, isLead: boolean) {
    setSaving(userId);
    try {
      await fetch(`/api/admin/departments/${dept.id}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, isLead }),
      });
      await loadMembers();
      onRefresh();
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="mt-3 border-t border-line pt-4 space-y-4">
      {/* Members list */}
      {loadingMembers ? (
        <div className="space-y-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-10 rounded-lg bg-panel animate-pulse" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <p className="text-xs text-ink-faint text-center py-4">
          {t('departments.members.empty')}
        </p>
      ) : (
        <div className="space-y-1.5">
          {members.map((m) => (
            <div
              key={m.userId}
              className="flex items-center gap-3 rounded-lg bg-panel px-3 py-2.5"
            >
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-600/25 text-xs font-bold text-accent-700 dark:text-accent-300">
                {m.email[0]?.toUpperCase() ?? '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink truncate">{m.email}</p>
              </div>
              <RoleBadge role={m.role} />
              {m.isLead && (
                <span title={t('common.roles.departmentLead')} className="text-warning-400">
                  <Star className="w-3.5 h-3.5 fill-current" />
                </span>
              )}
              {canEdit && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    disabled={saving === m.userId}
                    onClick={() => toggleLead(m.userId, !m.isLead)}
                    title={m.isLead ? t('departments.actions.removeLead') : t('departments.actions.makeLead')}
                    className={`p-1.5 rounded-lg text-xs transition-all ${
                      m.isLead
                        ? 'bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-300 hover:bg-warning-500/30'
                        : 'bg-panel text-ink-faint hover:bg-raised hover:text-warning-300'
                    } disabled:opacity-40`}
                  >
                    <Star className={`w-3.5 h-3.5 ${m.isLead ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    type="button"
                    disabled={saving === m.userId}
                    onClick={() => removeMember(m.userId)}
                    className="p-1.5 rounded-lg text-ink-faint hover:text-danger-400 hover:bg-danger-500/10 transition-all disabled:opacity-40"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add member */}
      {canEdit && (
        <div>
          {showAddUser ? (
            <div className="space-y-2">
              <input
                type="text"
                placeholder={t('departments.searchUserPlaceholder')}
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={inputCls}
              />
              {search.length > 0 && availableUsers.length === 0 && (
                <p className="text-xs text-ink-faint px-1">
                  {orgUsers.filter((u) => !memberIds.has(u.id)).length === 0
                    ? t('departments.members.allAlreadyAdded')
                    : t('departments.members.noMatch')}
                </p>
              )}
              {availableUsers.slice(0, 8).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  disabled={saving === u.id}
                  onClick={() => addMember(u.id)}
                  className="w-full flex items-center gap-3 rounded-lg bg-panel hover:bg-raised border border-line px-3 py-2.5 text-left transition-all disabled:opacity-40"
                >
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-600/25 text-xs font-bold text-accent-700 dark:text-accent-300">
                    {u.email[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink truncate">{u.email}</p>
                  </div>
                  <RoleBadge role={u.role} />
                  <UserPlus className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setShowAddUser(false); setSearch(''); }}
                className={btnGhost}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddUser(true)}
              className={btnGhost}
            >
              <UserPlus className="w-4 h-4" /> {t('departments.actions.addUser')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Departments page ─────────────────────────────────────────────────────

export default function DepartmentsPage() {
  const { t } = useAppTranslation();
  const { user } = useAuth();

  const [departments, setDepartments] = useState<Department[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  // Rename
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const canEdit = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'superadmin';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [deptsRes, usersRes] = await Promise.all([
        fetch('/api/admin/departments', { credentials: 'include' }),
        canEdit ? fetch('/api/admin/users', { credentials: 'include' }) : Promise.resolve(null),
      ]);
      const deptsData = await deptsRes.json() as { departments?: Department[] };
      setDepartments(deptsData.departments ?? []);
      if (usersRes && usersRes.ok) {
        const usersData = await usersRes.json() as OrgUser[];
        setOrgUsers(Array.isArray(usersData) ? usersData : []);
      }
    } catch {
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [canEdit]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError('');
    if (!newName.trim()) { setCreateError(t('departments.nameRequired')); return; }
    setCreating(true);
    try {
      const res = await fetch('/api/admin/departments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json() as { success?: boolean; error?: string; departmentId?: string };
      if (!res.ok || !data.success) { setCreateError(data.error ?? t('common.error')); return; }
      setShowCreate(false);
      setNewName('');
      setExpandedId(data.departmentId ?? null);
      await loadAll();
    } finally {
      setCreating(false);
    }
  }

  async function handleRename(deptId: string) {
    if (!renameName.trim()) return;
    setRenaming(true);
    try {
      await fetch(`/api/admin/departments/${deptId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: renameName.trim() }),
      });
      setRenameId(null);
      await loadAll();
    } finally {
      setRenaming(false);
    }
  }

  async function handleDelete(deptId: string, name: string) {
    if (!confirm(t('departments.confirmDelete', '', { name }))) return;
    await fetch(`/api/admin/departments/${deptId}`, { method: 'DELETE', credentials: 'include' });
    if (expandedId === deptId) setExpandedId(null);
    await loadAll();
  }

  return (
    <Layout title={t('departments.title')}>
      <div className="max-w-3xl space-y-5">

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-ink-muted">
            {loading ? '…' : t('departments.count', '', { count: departments.length })}
          </p>
          {canEdit && (
            <button onClick={() => setShowCreate(!showCreate)} className={btnPrimary}>
              + {t('departments.actions.create')}
            </button>
          )}
        </div>

        {/* Create form */}
        {showCreate && canEdit && (
          <div className="rounded-xl bg-panel border border-line p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink">{t('departments.createTitle')}</h2>
            <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-ink-muted mb-1.5">{t('common.name')}</label>
                <input
                  type="text"
                  required
                  maxLength={150}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className={inputCls}
                  placeholder={t('departments.namePlaceholder')}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={creating} className={btnPrimary}>
                  {creating ? t('departments.saving') : t('departments.save')}
                </button>
                <button type="button" onClick={() => { setShowCreate(false); setCreateError(''); }}
                  className="rounded-full border border-line px-4 py-2 text-sm text-ink-muted hover:text-ink transition-all">
                  {t('common.cancel')}
                </button>
              </div>
            </form>
            {createError && <p className="mt-3 text-sm text-danger-400">{createError}</p>}
          </div>
        )}

        {/* Departments list */}
        <div className="rounded-xl bg-panel border border-line overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-3">
              {[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-raised rounded-lg animate-pulse" />)}
            </div>
          ) : departments.length === 0 ? (
            <div className="p-10 text-center text-ink-faint">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">{t('departments.empty')}</p>
              {canEdit && (
                <p className="text-xs mt-1 text-ink-faint">{t('departments.emptyHint')}</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-line">
              {departments.map((dept) => {
                const isOpen = expandedId === dept.id;
                const isRenaming = renameId === dept.id;

                return (
                  <div key={dept.id}>
                    {/* Dept header row */}
                    <div className="flex items-center gap-3 px-5 py-4 hover:bg-raised transition-colors">
                      {/* Expand toggle */}
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : dept.id)}
                        className="flex-shrink-0 text-ink-faint hover:text-ink transition-colors"
                      >
                        {isOpen
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />}
                      </button>

                      {/* Name / rename inline */}
                      <div className="flex-1 min-w-0">
                        {isRenaming ? (
                          <form
                            onSubmit={(e) => { e.preventDefault(); handleRename(dept.id); }}
                            className="flex items-center gap-2"
                          >
                            <input
                              type="text"
                              value={renameName}
                              onChange={(e) => setRenameName(e.target.value)}
                              className="flex-1 rounded-lg bg-panel border border-accent-500/40 px-2 py-1 text-sm text-ink focus:outline-none"
                              autoFocus
                              maxLength={150}
                            />
                            <button type="submit" disabled={renaming}
                              className="p-1.5 rounded-lg bg-accent-100 dark:bg-accent-600/30 text-accent-700 dark:text-accent-300 hover:bg-accent-600/50 transition-all">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => setRenameId(null)}
                              className="p-1.5 rounded-lg text-ink-faint hover:text-ink transition-all">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </form>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setExpandedId(isOpen ? null : dept.id)}
                            className="text-left w-full"
                          >
                            <span className="font-medium text-ink text-sm">{dept.name}</span>
                          </button>
                        )}
                      </div>

                      {/* Lead badges */}
                      <div className="hidden sm:flex flex-wrap gap-1 max-w-[200px]">
                        {dept.leads.slice(0, 2).map((l) => (
                          <span key={l.userId} className="inline-flex items-center gap-1 rounded-full bg-warning-100 dark:bg-warning-500/15 text-warning-700 dark:text-warning-300 px-2 py-0.5 text-xs truncate max-w-[120px]">
                            <Star className="h-3 w-3 shrink-0 fill-current" /> {l.email.split('@')[0]}
                          </span>
                        ))}
                        {dept.leads.length > 2 && (
                          <span className="rounded-full bg-raised text-ink-muted px-2 py-0.5 text-xs">
                            +{dept.leads.length - 2}
                          </span>
                        )}
                      </div>

                      {/* Member count */}
                      <span className="text-xs text-ink-muted flex-shrink-0 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {dept.member_count}
                      </span>

                      {/* Actions */}
                      {canEdit && !isRenaming && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => { setRenameId(dept.id); setRenameName(dept.name); }}
                            className="p-1.5 rounded-lg text-ink-faint hover:text-ink hover:bg-raised transition-all"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(dept.id, dept.name)}
                            className="p-1.5 rounded-lg text-ink-faint hover:text-danger-400 hover:bg-danger-500/10 transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <DeptDetail
                          dept={dept}
                          orgUsers={orgUsers}
                          canEdit={canEdit}
                          onRefresh={loadAll}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info for leads */}
        {user?.role === 'department_lead' && (
          <div className="rounded-xl bg-warning-500/10 border border-warning-500/20 px-4 py-3">
            <p className="text-xs text-warning-300/80">
              {t('departments.leadInfo')}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
